// dwsPopupEngine: the one positioning engine for everything that floats beside an anchor (DwsSelect,
// DwsAutoComplete, the pickers, DwsPopover, DwsTooltip). Core: several modules call it synchronously.

import { publish } from "../shared/publish";

// True when an anchor (a select trigger, an autocomplete field) is scrolled out of sight inside a clipping ancestor -
// the page's scrolling .basepage under its sticky header, a dialog's scrolling content - or out of the viewport.
// A popup raised into the top layer escapes those clips, so without this check it stayed on screen, floating over the
// header or the dialog title, while its field had long scrolled away.
// stickyOnly narrows the "something else is on top" test to a position: sticky bar (or something inside one): a toast,
// a dialog or any other fixed or top-layer element passing over the field for a moment then does not count. For a
// popup that closes for good when its field is covered (DwsDateTimePicker) rather than hiding until it is back.
function DwsIsAnchorClippedOut(anchor: Element | null, owner?: Element | null, stickyOnly?: boolean): boolean {
    if (!anchor || !anchor.isConnected) {
        return true;
    }

    let rect = anchor.getBoundingClientRect();
    let top = 0;
    let left = 0;
    let bottom = document.documentElement.clientHeight;
    let right = document.documentElement.clientWidth;

    for (let el = anchor.parentElement; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        let style = getComputedStyle(el);

        if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
            let box = el.getBoundingClientRect();
            top = Math.max(top, box.top);
            left = Math.max(left, box.left);
            bottom = Math.min(bottom, box.bottom);
            right = Math.min(right, box.right);
        }

        // Fixed elements (a dialog, a drawer) are clipped by the viewport only, not by the page behind them.
        if (style.position === 'fixed') {
            break;
        }
    }

    // Hidden once less than a few pixels of the anchor are left, so a popup never points at an invisible field.
    let visibleHeight = Math.min(rect.bottom, bottom) - Math.max(rect.top, top);
    let visibleWidth = Math.min(rect.right, right) - Math.max(rect.left, left);

    if (visibleHeight < 4 || visibleWidth < 4) {
        return true;
    }

    // A sticky bar inside the same scroller (the demo's breadcrumb) covers the field without clipping it: test what
    // is actually on top at the middle of the visible part. Anything inside owner (the component, which also holds
    // the popup in the DOM) counts as the field itself.
    if (owner && typeof document.elementFromPoint === 'function') {
        let x = (Math.max(rect.left, left) + Math.min(rect.right, right)) / 2;
        let y = (Math.max(rect.top, top) + Math.min(rect.bottom, bottom)) / 2;
        let hit = document.elementFromPoint(x, y);

        if (hit && !owner.contains(hit) && !hit.contains(owner)) {
            if (!stickyOnly) {
                return true;
            }

            for (let cover = hit; cover && cover !== document.body && cover !== document.documentElement; cover = cover.parentElement) {
                if (getComputedStyle(cover).position === 'sticky') {
                    return true;
                }
            }
        }
    }

    return false;
}

// ---------------------------------------------------------------------------------------------------------------
// dwsPopupEngine: one positioning engine for everything that floats beside an anchor (DwsSelect, DwsAutoComplete,
// the pickers, DwsPopover, DwsTooltip). Each of them used to carry its own copy of the same decisions - which side,
// when to flip, how far to shift, when to hide because the anchor is gone, how to follow a scrolling page - and a fix
// in one did not reach the others.
//
// The engine knows nothing about the components. A component hands it the popup element, the anchor element and
// options, and decides itself what to do with the result (classes, arrow, announcing):
//
//   compute(anchorRect, width, height, options)  pure geometry: where a box of that size goes
//   insets()                                     room taken by fixed / sticky page headers and footers
//   isAnchorHidden(anchor, owner, stickyOnly)    the anchor is scrolled away or covered
//   raise(element)                               into the top layer (popover="manual") where the browser has one
//   follow(popup, anchor, place, lost)           calls place() whenever the anchor may have moved, until the popup
//                                                leaves the document (no call needed to stop; lost() then tells
//                                                a component that wants to look its elements up again)
//   unfollow(popup)
// ---------------------------------------------------------------------------------------------------------------
publish("dwsPopupEngine", {
    _entries: [],
    _listening: false,
    _frame: 0,
    _scheduled: false,

    clamp: function (value, min, max) {
        // When the box is larger than the room (max < min) the start edge wins: its beginning stays readable.
        return max < min ? min : Math.min(Math.max(value, min), max);
    },

    // The part of the window a popup may use: the visual viewport where the browser reports one (an on-screen
    // keyboard shrinks it but not the layout viewport), less the padding and the page's fixed headers and footers.
    viewport: function (pad, useInsets) {
        const vv = window.visualViewport;
        const width = document.documentElement.clientWidth;
        const top = vv ? vv.offsetTop : 0;
        const height = vv ? vv.height : document.documentElement.clientHeight;
        const inset = useInsets ? window.dwsPopupEngine.insets() : { top: 0, bottom: 0 };
        const padding = Math.max(0, pad || 0);

        return {
            left: padding,
            right: width - padding,
            top: Math.max(top, inset.top) + padding,
            bottom: Math.min(top + height, document.documentElement.clientHeight - inset.bottom) - padding
        };
    },

    // Space taken by fixed headers and footers: the library's sticky headers stacked from the top edge (the app's
    // .dws-sticky-top-banner and a page's .dws-navbar-sticky-top below it), plus whatever an app declares in
    // --dws-popover-viewport-inset-top / -bottom on :root.
    insets: function () {
        const rootStyle = getComputedStyle(document.documentElement);
        let top = parseFloat(rootStyle.getPropertyValue("--dws-popover-viewport-inset-top")) || 0;
        const bottom = parseFloat(rootStyle.getPropertyValue("--dws-popover-viewport-inset-bottom")) || 0;
        const headers = [];

        document.querySelectorAll(".dws-sticky-top-banner, .dws-navbar-sticky-top").forEach(function (header) {
            const position = getComputedStyle(header).position;
            const r = header.getBoundingClientRect();

            if ((position === "fixed" || position === "sticky") && r.height > 0) {
                headers.push(r);
            }
        });

        // Only headers that touch the ones above them count: a sticky header scrolled down with its page does not.
        headers.sort(function (a, b) { return a.top - b.top; });
        headers.forEach(function (r) {
            if (r.top <= top + 1) {
                top = Math.max(top, r.bottom);
            }
        });

        return { top: Math.max(0, top), bottom: Math.max(0, bottom) };
    },

    // Where a box of width x height goes beside the anchor rectangle.
    //   side        "bottom" | "top" | "left" | "right"                       (default "bottom")
    //   align       "start" | "center" | "end" along the anchor's edge        (default "center")
    //   offset      gap between anchor and box                                (default 0)
    //   cross       shift along the anchor's edge                             (default 0)
    //   flip        take the opposite side when the box does not fit and that side has more room
    //   shift       move the box along the edge to stay inside the bounds, never so far that it leaves its anchor
    //   attach      least overlap with the anchor's edge kept while shifting  (default 0; an arrow needs its size)
    //   bounds      { left, top, right, bottom } the box may use              (default: viewport(0, false))
    // Returns { x, y, side, flipped, space, clear }: space is the room on the chosen side (for capping the size), and
    // clear says whether the box ended up clear of the anchor on that side (false when it had to lie over it).
    compute: function (a, width, height, options) {
        const engine = window.dwsPopupEngine;
        const o = options || {};
        const bounds = o.bounds || engine.viewport(0, false);
        const offset = o.offset || 0;
        const cross = o.cross || 0;
        const attach = o.attach || 0;
        const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" };
        const space = {
            top: a.top - bounds.top - offset,
            bottom: bounds.bottom - a.bottom - offset,
            left: a.left - bounds.left - offset,
            right: bounds.right - a.right - offset
        };
        const needed = function (s) { return s === "top" || s === "bottom" ? height : width; };
        let side = o.side || "bottom";
        let flipped = false;

        if (o.flip && needed(side) > space[side] && space[opposite[side]] > space[side]) {
            side = opposite[side];
            flipped = true;
        }

        const vertical = side === "top" || side === "bottom";
        const align = o.align || "center";
        let x;
        let y;

        if (vertical) {
            y = side === "top" ? a.top - offset - height : a.bottom + offset;
            x = (align === "start" ? a.left : align === "end" ? a.right - width : a.left + (a.width - width) / 2) + cross;

            if (o.shift) {
                x = engine.clamp(x, bounds.left, bounds.right - width);
                x = engine.clamp(x, a.left - width + attach, a.right - attach);
                // Last resort along the main axis: a box too large for either side lies over its anchor, inside the bounds.
                y = engine.clamp(y, bounds.top, bounds.bottom - height);
            }
        } else {
            x = side === "left" ? a.left - offset - width : a.right + offset;
            y = (align === "start" ? a.top : align === "end" ? a.bottom - height : a.top + (a.height - height) / 2) + cross;

            if (o.shift) {
                y = engine.clamp(y, bounds.top, bounds.bottom - height);
                y = engine.clamp(y, a.top - height + attach, a.bottom - attach);
                x = engine.clamp(x, bounds.left, bounds.right - width);
            }
        }

        const overlaps = vertical
            ? y < a.bottom - 1 && y + height > a.top + 1
            : x < a.right - 1 && x + width > a.left + 1;

        return { x: x, y: y, side: side, flipped: flipped, space: space[side], clear: !overlaps };
    },

    // True when the anchor is scrolled out of its clipping containers or the window, or - with an owner - when
    // something else lies on top of it (a sticky bar it scrolled under). See DwsIsAnchorClippedOut, which this wraps
    // so that there is one definition.
    isAnchorHidden: function (anchor, owner, stickyOnly) {
        return DwsIsAnchorClippedOut(anchor, owner, stickyOnly);
    },

    // Raises the element into the top layer, where no ancestor's overflow, transform or z-index reaches it. Returns
    // whether it is there; without the Popover API the caller keeps its in-flow fallback.
    raise: function (element) {
        if (!element || typeof element.showPopover !== "function" || !element.hasAttribute("popover")) {
            return false;
        }

        try {
            if (!element.matches(":popover-open")) {
                element.showPopover();
            }

            return true;
        } catch (e) {
            return false;
        }
    },

    // Keeps a popup at its anchor: place() runs when the page scrolls (any scroller), the window or the visual
    // viewport resizes, the popup or the anchor changes size, and when the anchor moves for any other reason (a
    // drawer sliding in, content loading above it) - that last case is caught by comparing the anchor's box once per
    // frame while a popup is open. Scrolling inside the popup itself moves nothing. The entry removes itself when
    // the popup or the anchor leaves the document.
    follow: function (popup, anchor, place, lost) {
        const engine = window.dwsPopupEngine;

        engine.unfollow(popup);

        const entry = { popup: popup, anchor: anchor, place: place, lost: lost || null, observer: null, rect: null };

        if (typeof ResizeObserver === "function") {
            entry.observer = new ResizeObserver(function () { engine._schedule(); });
            entry.observer.observe(popup);
            entry.observer.observe(anchor);
        }

        engine._entries.push(entry);
        engine._listen();
        engine._run(entry);

        return entry;
    },

    unfollow: function (popup) {
        const engine = window.dwsPopupEngine;

        for (let i = engine._entries.length - 1; i >= 0; i--) {
            if (engine._entries[i].popup === popup) {
                if (engine._entries[i].observer) {
                    engine._entries[i].observer.disconnect();
                }

                engine._entries.splice(i, 1);
            }
        }

        if (engine._entries.length === 0) {
            engine._unlisten();
        }
    },

    isFollowing: function (popup) {
        return window.dwsPopupEngine._entries.some(function (entry) { return entry.popup === popup; });
    },

    _run: function (entry) {
        const r = entry.anchor.getBoundingClientRect();

        entry.rect = { left: r.left, top: r.top, width: r.width, height: r.height };

        try {
            entry.place();
        } catch (e) {
            // A component's place function failing must not stop the others from following.
            if (window.console && console.warn) {
                console.warn("dwsPopupEngine: place failed", e);
            }
        }
    },

    _placeAll: function () {
        const engine = window.dwsPopupEngine;

        engine._scheduled = false;

        // A copy: a place function may close its popup, which changes the list.
        engine._entries.slice().forEach(function (entry) {
            if (!entry.popup.isConnected || !entry.anchor.isConnected) {
                engine.unfollow(entry.popup);

                if (entry.lost) {
                    entry.lost();
                }

                return;
            }

            engine._run(entry);
        });
    },

    // One placement per frame, however many events arrive in it.
    _schedule: function () {
        const engine = window.dwsPopupEngine;

        if (engine._scheduled) {
            return;
        }

        engine._scheduled = true;
        requestAnimationFrame(engine._placeAll);
    },

    _onScroll: function (e) {
        const engine = window.dwsPopupEngine;
        const target = e && e.target && e.target.nodeType === 1 ? e.target : null;

        // The list scrolling inside a popup moves no anchor.
        if (target && engine._entries.some(function (entry) { return entry.popup.contains(target); })
            && !engine._entries.some(function (entry) { return target.contains(entry.anchor); })) {
            return;
        }

        // Placed in the same frame as the scroll, so the popup does not trail its anchor by a frame.
        engine._placeAll();
    },

    _watch: function () {
        const engine = window.dwsPopupEngine;

        engine._frame = 0;

        if (engine._entries.length === 0) {
            return;
        }

        let moved = false;

        engine._entries.slice().forEach(function (entry) {
            if (!entry.popup.isConnected || !entry.anchor.isConnected) {
                engine.unfollow(entry.popup);

                if (entry.lost) {
                    entry.lost();
                }

                return;
            }

            const r = entry.anchor.getBoundingClientRect();
            const last = entry.rect;

            if (!last || Math.abs(r.left - last.left) > 0.5 || Math.abs(r.top - last.top) > 0.5
                || Math.abs(r.width - last.width) > 0.5 || Math.abs(r.height - last.height) > 0.5) {
                moved = true;
            }
        });

        if (moved) {
            engine._placeAll();
        }

        if (engine._entries.length > 0) {
            engine._frame = requestAnimationFrame(engine._watch);
        }
    },

    _listen: function () {
        const engine = window.dwsPopupEngine;

        if (engine._listening) {
            return;
        }

        engine._listening = true;
        window.addEventListener("scroll", engine._onScroll, true);
        window.addEventListener("resize", engine._schedule);

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", engine._schedule);
            window.visualViewport.addEventListener("scroll", engine._schedule);
        }

        engine._frame = requestAnimationFrame(engine._watch);
    },

    _unlisten: function () {
        const engine = window.dwsPopupEngine;

        if (!engine._listening) {
            return;
        }

        engine._listening = false;
        window.removeEventListener("scroll", engine._onScroll, true);
        window.removeEventListener("resize", engine._schedule);

        if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", engine._schedule);
            window.visualViewport.removeEventListener("scroll", engine._schedule);
        }

        if (engine._frame) {
            cancelAnimationFrame(engine._frame);
            engine._frame = 0;
        }
    }
});

publish("DwsIsAnchorClippedOut", DwsIsAnchorClippedOut);
